import { Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router";

import {
  CROSS_TENANT_INVITATION_SESSION_KEY,
  INVITATION_ID_KEY,
  RanaAuthorisationBackground,
  RanaButton,
  RanaHeading,
  RanaInfoBox,
  RanaLoading,
  RanaLogoLarge,
  RanaLogoPrimary,
  SIZES,
  SystemStatusPage,
  chosenIdentityProviderKey,
} from "@nens/ranalib";

import { useInvitationAcceptPage } from "../hooks/use-invitation-accept-page";

import { CreateNewAccountForm } from "./create-new-account-form";

export function InvitationAcceptPage() {
  const { invitationId, tenant } = useParams();
  const navigate = useNavigate();

  // User comes here from a link in an invitation email.

  // First, we CHECK if the invitation is still valid by request an invitation check at
  // /invitations/{invitation_id}/check. If the invitation is valid, we show the user options
  // to either create an account or to log in with an existing account and save the invitation
  // id in the session storage.

  // Secondly, user should be presented with three options:
  // 1. Login with a corporate account
  // 2. Login with a Rana account
  // 3. Create a new Rana account => POSTed to /tenants/{tenant_id}/accounts

  // After an option is selected, we PUT the user in tenant via /tenants/{tenant_id}/users/{user_id}
  // with an invitation id. This PUT request may give an error and we show an error screen in this
  // case. If no error, redirect to the dashboard page.

  // Finally, accept the invitation once the dashboard is loaded. This is done by a PUT request to
  // /tenants/{tenant_id}/invitations/{invitation_id}/accept. This PUT request may give an error.
  // Show the error or success message to the user in a toast.

  const {
    isLoggedIn,
    invitation,
    isLoading,
    isError,
    redirectToLogin,
    pageType,
    setPageType,
    identityProviders,
    identityProvidersLoaded,
    createAccount,
    accountLoading,
    accountSuccess,
    accountError,
    accountErrors,
  } = useInvitationAcceptPage(tenant ?? "", invitationId);

  const loginButton = {
    text: <FormattedMessage id="button.to_login" defaultMessage="Go to the login page" />,
    onClick: () => redirectToLogin(),
  };

  const ranaLoginButton = {
    text: <FormattedMessage id="button.to_login" defaultMessage="Go to the login page" />,
    onClick: () => redirectToLogin("rana"),
  };

  if (isLoading) return <RanaLoading h="vh" isCentered />;

  if (!invitation || isError) {
    // The invitation has been expired or is invalid.
    return (
      <RanaInfoBox
        title={<FormattedMessage id="error.invitation_stale" defaultMessage="Invalid invitation" />}
        errors={{ error: "The invitation has been expired or is invalid." }}
        button={loginButton}
      />
    );
  } else if (invitation) {
    // We have a correct invitation.

    // Save the invitation id in the session storage.
    window.sessionStorage.setItem(INVITATION_ID_KEY, invitation.id);

    // If user is already logged in and is the same as the invited user,
    // we navigate to the tenant dashboard page. Else we log the user out
    // and show the different login options.
    if (isLoggedIn) {
      navigate(`/${tenant}`);
      return null;
    }

    // If user has chosen an identity provider earlier, forget it.
    window.localStorage.removeItem(chosenIdentityProviderKey(tenant ?? ""));

    if (pageType === "sign-up") {
      if (accountSuccess) {
        // We know the user just made a Rana account, so go the login page with
        // the Rana identity provider selected.
        return (
          <RanaInfoBox
            title={
              <FormattedMessage
                id="heading.account_created_success"
                defaultMessage="Rana account successfully created"
              />
            }
            button={ranaLoginButton}
          />
        );
      } else if (accountError || accountErrors) {
        return (
          <RanaInfoBox
            title={
              <FormattedMessage
                id="heading.account_created_error"
                defaultMessage="Account creation failed."
              />
            }
            errors={accountErrors}
            button={loginButton}
          />
        );
      } else {
        return (
          <RanaAuthorisationBackground
            pageSubTitle={
              <FormattedMessage id="page_title.registration" defaultMessage="Registration" />
            }
          >
            <Flex
              direction="column"
              w="full"
              maxWidth="lg"
              background="brand.light_sky"
              padding={6}
              mb={24}
            >
              <RanaLogoPrimary w="md" />
              <RanaHeading type="h4" my={8}>
                <FormattedMessage
                  id="heading.create_new_account"
                  defaultMessage="Please fill in the information to continue"
                />
              </RanaHeading>
              <CreateNewAccountForm
                invitation={invitation}
                onCancel={() => {
                  setPageType("sign-in");
                }}
                isDisabled={accountLoading}
                onSubmit={createAccount}
              />
            </Flex>
          </RanaAuthorisationBackground>
        );
      }
    } else if (pageType === "sign-in") {
      return (
        <SystemStatusPage
          variant="login"
          identityProviders={identityProviders}
          redirectToLogin={redirectToLogin}
          onCancel={() => {
            setPageType("sign-up");
          }}
        />
      );
    } else {
      const existingUser = invitation.existingUser;

      // Show the options.
      return (
        <RanaAuthorisationBackground
          pageSubTitle={<FormattedMessage id="page_title.invitation" defaultMessage="Invitation" />}
        >
          <RanaLogoLarge color="brand.clear_creek" mt={4} />
          <RanaHeading type="h3" mt={4} mb={11}>
            <FormattedMessage id="invitation.welcome" defaultMessage="Hi, welcome to rana" />
          </RanaHeading>
          {identityProvidersLoaded && invitation ? (
            <Grid
              templateColumns={{ base: "2xs", md: `${SIZES["2xs"]} ${SIZES["2xs"]}` }}
              gap={8}
              mb={24}
            >
              {existingUser ? (
                <GridItem
                  colSpan={{ base: 1, md: 2 }}
                  bg="brand.light_sky"
                  borderRadius="base"
                  borderWidth="base"
                  borderColor="brand.border"
                  padding={6}
                >
                  <Text textStyle="md" mb={4}>
                    <FormattedMessage
                      id="invitation.existing_user_prompt"
                      defaultMessage="Do you want to accept this invitation using the account from {identityProviderName} that is already in use with {tenantName}?"
                      values={{
                        identityProviderName: existingUser.identityProviderName,
                        tenantName: existingUser.tenantName,
                      }}
                    />
                  </Text>
                  <RanaButton
                    variant="primary"
                    onClick={() => {
                      window.sessionStorage.setItem(CROSS_TENANT_INVITATION_SESSION_KEY, "true");
                      redirectToLogin(existingUser.identityProviderId);
                    }}
                  >
                    <FormattedMessage
                      id="button.accept_with_existing_account"
                      defaultMessage="Continue with {identityProviderName}"
                      values={{
                        identityProviderName: existingUser.identityProviderName,
                      }}
                    />
                  </RanaButton>
                </GridItem>
              ) : null}
              <GridItem
                position="relative"
                bg="brand.light_sky"
                borderRadius="base"
                borderWidth="base"
                borderColor="brand.border"
                h="2xs"
                padding={6}
              >
                <RanaHeading type="h4" mb={2}>
                  <FormattedMessage id="heading.are_you_new" defaultMessage="Are you new?" />
                </RanaHeading>
                <Text textStyle="md">
                  <FormattedMessage
                    id="text.create_new_account"
                    defaultMessage="Create your own account and start your rana adventure"
                  />
                </Text>
                <RanaButton
                  variant="primary"
                  position="absolute"
                  bottom={6}
                  right={6}
                  onClick={() => setPageType("sign-up")}
                >
                  <FormattedMessage id="button.sign_up" defaultMessage="Sign up to rana" />
                </RanaButton>
              </GridItem>
              <GridItem
                position="relative"
                bg="brand.light_sky"
                borderRadius="base"
                borderWidth="base"
                borderColor="brand.border"
                h="2xs"
                padding={6}
              >
                <RanaHeading type="h4" mb={2}>
                  <FormattedMessage
                    id="heading.already_have_account"
                    defaultMessage="Already have an account?"
                  />
                </RanaHeading>
                <Text textStyle="md">
                  <FormattedMessage id="text.sign_in_here" defaultMessage="Sign in here!" />
                  <br />
                  <FormattedMessage
                    id="text.please_also_use_this_option"
                    defaultMessage="Please also use this option if you have an existing Lizard or 3Di account."
                  />
                </Text>
                <RanaButton
                  variant="primary"
                  position="absolute"
                  bottom={6}
                  right={6}
                  onClick={() => setPageType("sign-in")}
                >
                  <FormattedMessage id="button.sign_in" defaultMessage="Sign in to rana" />
                </RanaButton>
              </GridItem>
            </Grid>
          ) : (
            <RanaLoading isCentered />
          )}
        </RanaAuthorisationBackground>
      );
    }
  } else {
    // Shouldn't happen.
    console.error("Entered impossible case in invitation-accept-page");
    redirectToLogin();
    return null;
  }
}
